import { useCallback, useContext, useEffect, useState } from 'react';
import { getActiveSurveys, ActiveSurveyPhase } from '../services/api/surveys';
import { getCurrentSession } from '../services/api/auth';
import { SurveyStatusContext, type SurveyStatus } from './surveyStatus';

/**
 * Pide las encuestas del jugador. La usa SurveyStatusProvider; el resto de la
 * app lee el resultado compartido con `usePendingSurveys`.
 * `enabled = false` no pide nada (queda vacío).
 */
export function useSurveyStatusSource(enabled = true): SurveyStatus {
  const [pendingPhases, setPendingPhases] = useState<ActiveSurveyPhase[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [initialized, setInitialized] = useState(false);

  const refresh = useCallback(async (): Promise<ActiveSurveyPhase[]> => {
    try {
      setLoading(true);
      const session = getCurrentSession();
      if (!session?.token) {
        setPendingPhases([]);
        return [];
      }
      const data = await getActiveSurveys(session.token);
      const pending = data.phases.filter(
        (p) => p.is_open && p.user_can_access && !p.already_submitted
      );
      setPendingPhases(pending);
      return pending;
    } catch (e) {
      console.error('Error cargando encuestas pendientes:', e);
      setPendingPhases([]);
      return [];
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  return {
    pendingPhases,
    hasPending: pendingPhases.length > 0,
    mustAnswer: pendingPhases.find((p) => p.must_answer) ?? null,
    loading,
    initialized,
    refresh,
  };
}

/** Encuestas pendientes: las compartidas del provider o, fuera de él, propias. */
export function usePendingSurveys(): SurveyStatus {
  const shared = useContext(SurveyStatusContext);
  const local = useSurveyStatusSource(!shared);
  return shared ?? local;
}
